import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import {
  createConfigParamTypeExistingColumn,
  createConfigParamTypeExistingColumnList,
  ETLDryRunObjectTableAddColumn,
} from '../utils';
import { addDryRunObjectTable, getETLComponentInputObjectTable, getETLTableObjectColumn } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';

// Import Shared Logic
import { TpJsonColumnInfo, applyJsonColumnsToDryRun, JsonColumnEditorList } from './etlJsonUtils';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  try {
    const colIdList = (comp.params['transferColumns'] || []) as string[];
    for (const colId of colIdList) {
      const sourceColInfo = getETLTableObjectColumn(inputTableObj, colId);
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        sourceColInfo.identifier,
        sourceColInfo.name,
        sourceColInfo.description,
        sourceColInfo.dataType,
      );
    }
  } catch (error) {
    addETLDryRunError(dryRun, String(error), comp.id, 'transferColumns');
  }

  const jsonColumns = (comp.params['jsonColumns'] || []) as TpJsonColumnInfo[];
  applyJsonColumnsToDryRun(outputTableObj, jsonColumns, dryRun, comp.id);

  return { abort: false };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const jsonColumns = (comp.params['jsonColumns'] || []) as TpJsonColumnInfo[];

  return (
    <JsonColumnEditorList
      title="JSON Columns"
      description="Define keys from the JSON key-value dictionaries to convert into columns."
      columns={jsonColumns}
      dryRun={dryRun}
      compId={comp.id}
      onChange={(newCols) => {
        comp.params['jsonColumns'] = newCols;
        updateEditingComponent(comp);
      }}
    />
  );
}

export const manifestETLJsonToTable: TpETLComponentTypeManifest = {
  id: 'JsonToTable',
  name: 'Convert JSON to table',
  description:
    'Converts a JSON column with a list of key-value dictionaries to a table. The result table will contain the combination of all rows from all JSON fields in the source table.',
  categoryId: 'json',
  icon: <FontAwesomeIcon icon={['fas', 'table']} />,
  inputs: [{ type: TpETLObjectTypes.TABLE, id: 'sourceTable', name: 'Source Table', columns: [] }],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Table with the output of the JSON conversion',
      columns: [],
    },
  ],
  params: [
    {
      id: 'jsonSourceColumn',
      name: 'JSON source column',
      description:
        'Column with the source JSON, structured as a list of dictionary key-value pairs. Each element in the JSON list will become a row in the output table, with columns taken from the key-value pairs.',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
    {
      id: 'transferColumns',
      name: 'Transfer columns',
      description: 'Columns from the source table to be copied to the output table.',
      paramType: createConfigParamTypeExistingColumnList('sourceTable'),
    },
  ],
  performDryRun,
  renderCustomConfigParams,
};
